/* Step-list strutturata: elenco ordinato dei passi con indentazione per blocco,
   selezione, sposta su/giù, elimina e aggiunta di nuovi passi. */
import type { DiagramDoc, FlowStep } from "@fullsteps/shared";
import { el } from "../render/svg.js";
import { canMove } from "./ops.js";
import { depths, STEP_LABEL, stepSummary } from "./describe.js";

export type AddKind =
  | "msg"
  | "note"
  | "act"
  | "deact"
  | "create"
  | "destroy"
  | "phase"
  | "alt"
  | "opt"
  | "loop";

export interface StepListHandlers {
  onSelect: (index: number) => void;
  onDelete: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onAddAfter: (index: number, kind: AddKind) => void;
}

const ADD_BUTTONS: [AddKind, string][] = [
  ["msg", "＋ Messaggio"],
  ["note", "＋ Nota"],
  ["act", "＋ Attiva"],
  ["deact", "＋ Disattiva"],
  ["create", "＋ Crea"],
  ["destroy", "＋ Distruggi"],
  ["phase", "＋ Fase"],
  ["alt", "＋ ALT"],
  ["opt", "＋ OPT"],
  ["loop", "＋ LOOP"],
];

export function renderStepList(
  container: HTMLElement,
  doc: DiagramDoc,
  selIndex: number | null,
  h: StepListHandlers,
): void {
  container.textContent = "";

  const addbar = el("div", "ed-addbar");
  const at = selIndex != null ? selIndex : doc.flow.length - 1;
  for (const [kind, label] of ADD_BUTTONS) {
    const b = el("button", "ed-add", label) as HTMLButtonElement;
    b.type = "button";
    b.title = `Inserisci dopo il passo selezionato`;
    b.addEventListener("click", () => h.onAddAfter(at, kind));
    addbar.appendChild(b);
  }
  container.appendChild(addbar);

  const list = el("div", "ed-steplist");
  const dps = depths(doc.flow);
  doc.flow.forEach((step, i) => {
    const row = el("div", "ed-step" + (i === selIndex ? " sel" : ""));
    row.style.setProperty("--depth", String(dps[i] ?? 0));
    row.tabIndex = 0;

    const badge = el("span", "ed-badge ed-t-" + step.t, badgeText(step));
    const sum = el("span", "ed-step-sum", stepSummary(step, doc));
    sum.title = STEP_LABEL[step.t] + " · " + stepSummary(step, doc);
    row.append(badge, sum);

    const actions = el("span", "ed-step-actions");
    const mkAct = (label: string, title: string, disabled: boolean, fn: () => void): HTMLButtonElement => {
      const b = el("button", "ed-iconbtn", label) as HTMLButtonElement;
      b.type = "button";
      b.title = title;
      b.disabled = disabled;
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        fn();
      });
      return b;
    };
    actions.append(
      mkAct("↑", "Sposta su", !canMove(doc, i, -1), () => h.onMove(i, -1)),
      mkAct("↓", "Sposta giù", !canMove(doc, i, 1), () => h.onMove(i, 1)),
      mkAct("🗑", step.t === "else" || step.t === "end" ? "Non eliminabile" : "Elimina", step.t === "else" || step.t === "end", () => h.onDelete(i)),
    );
    row.appendChild(actions);

    row.addEventListener("click", () => h.onSelect(i));
    row.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        h.onSelect(i);
      }
    });
    list.appendChild(row);
  });
  container.appendChild(list);

  // porta in vista il passo selezionato
  if (selIndex != null) {
    const sel = list.children[selIndex] as HTMLElement | undefined;
    sel?.scrollIntoView({ block: "nearest" });
  }
}

function badgeText(step: FlowStep): string {
  switch (step.t) {
    case "msg":
      return "MSG";
    case "note":
      return "NOTA";
    case "act":
      return "ACT";
    case "deact":
      return "DEACT";
    case "create":
      return "NEW";
    case "destroy":
      return "DEL";
    case "phase":
      return "FASE";
    case "block":
      return step.kind.toUpperCase();
    case "else":
      return "ELSE";
    case "end":
      return "END";
  }
}
